import { Link as HeroLink } from '@heroui/react';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';

import { useNotification } from '../../../../contexts/NotificationContext';
import { useCreateBuyer } from '../../hooks';
import { getCreateBuyerFailureCopy } from '../../utils';
import { BuyerForm } from '../BuyerForm';
import type { BuyerFormData } from '../BuyerForm';

export const CreateBuyer: React.FC = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  // Goes through the page's data hook rather than the generated mutation: the
  // hook is the one place that maps form fields to the backend's address
  // contract. The inline mapping this replaced sent the establishment place id
  // as `googlePlaceAddressId`, never sent `googlePlaceId` at all, and dropped
  // falsy coordinates — so a custom buyer stored no address id and a UTC+0
  // buyer stored no offset (CRM-360).
  const { createBuyer } = useCreateBuyer({});

  // Failures are surfaced here and rethrown: the form's submit wrapper reads the
  // rejection as "keep the values, stop the spinner" (CRM-376), and it is the
  // only failure channel the form contract has. It contains the rejection, so
  // rethrowing costs nothing.
  //
  // On success it returns nothing: creation navigates away, so there is no form
  // left to rebaseline. Only the edit form uses the returned snapshot.
  const handleSubmit = async (data: BuyerFormData) => {
    try {
      const created = await createBuyer({
        name: data.name,
        displayName: data.displayName || undefined,
        fullAddress: data.fullAddress,
        deliveryComment: data.deliveryComment || undefined,
        addressCity: data.addressCity || undefined,
        addressCountry: data.addressCountry || undefined,
        isTest: data.isTest,
        addressLatitude: data.addressLatitude,
        addressLongitude: data.addressLongitude,
        addressUTCOffset: data.addressUTCOffset,
        addressSource: data.addressSource || 'custom',
        addressGooglePlaceId: data.addressGooglePlaceId,
        addressGoogleAddressId: data.addressGoogleAddressId,
      });

      if (created?.id) {
        navigate(`/buyers/${created.id}`);
      } else {
        navigate('/buyers');
      }
    } catch (error) {
      const { title, message } = getCreateBuyerFailureCopy(
        error instanceof Error ? error.message : ''
      );

      addNotification({ title, message, type: 'error' });
      throw error;
    }

    return undefined;
  };

  const handleCancel = () => {
    navigate('/buyers');
  };

  return (
    <>
      <Helmet>
        <title>Create Buyer - Choco Admin</title>
      </Helmet>
      <div className="p-6 min-h-screen bg-background">
        <div className="mb-6">
          <HeroLink
            as={Link}
            to="/buyers"
            color="primary"
            className="inline-flex items-center gap-2 mb-4"
          >
            ← Back to Buyers
          </HeroLink>
          <h1 className="title-large mb-2">Create New Buyer</h1>
          <p className="text-default-600">
            Add a new buyer to the system. Start by searching for their business
            location.
          </p>
        </div>

        <BuyerForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          title="Buyer Information"
        />
      </div>
    </>
  );
};
